import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, Button, Input, Field, FieldLabel } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import toast from 'react-hot-toast';
import type { MarketingCampaign, MarketingCampaignRecipient } from '@court-sync/shared';

export default function CampaignDetailPage() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState<MarketingCampaign | null>(null);
  const [recipients, setRecipients] = useState<MarketingCampaignRecipient[]>([]);
  const [previewEmail, setPreviewEmail] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [sending, setSending] = useState(false);

  const load = () => {
    if (!id) return;
    adminBackofficeApi.getCampaign(id).then((res) => {
      if (res.success) {
        setCampaign(res.data.campaign);
        setRecipients(res.data.recipients || []);
      }
    });
  };

  useEffect(() => {
    load();
  }, [id]);

  const handlePreviewRecipients = async () => {
    if (!id || !campaign) return;
    const res = await adminBackofficeApi.previewCampaignRecipients(id, campaign.audienceConfig as unknown as Record<string, unknown>);
    if (res.success) setRecipientCount(res.data.count);
  };

  const handlePreviewEmail = async () => {
    if (!id || !previewEmail) return;
    const res = await adminBackofficeApi.previewCampaignEmail(id, previewEmail);
    if (res.success) setPreviewHtml(res.data.html);
  };

  const pollUntilComplete = async (campaignId: string) => {
    for (let attempt = 0; attempt < 90; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const res = await adminBackofficeApi.getCampaign(campaignId);
      if (!res.success) continue;
      setCampaign(res.data.campaign);
      setRecipients(res.data.recipients || []);
      const status = res.data.campaign.status;
      if (status === 'sent') {
        toast.success(
          `Campaign sent: ${res.data.campaign.sentCount}/${res.data.campaign.recipientsCount}`
        );
        return;
      }
      if (status === 'failed') {
        toast.error('Campaign send failed. Check recipient errors and retry.');
        return;
      }
    }
    toast.error('Campaign send is still in progress. Refresh this page later.');
  };

  const handleSend = async () => {
    if (!id) return;
    if (!window.confirm('Send this campaign to all opted-in recipients?')) return;
    setSending(true);
    try {
      const res = await adminBackofficeApi.sendCampaign(id);
      if (res.success) {
        toast.success('Campaign send started');
        setCampaign((prev) => (prev ? { ...prev, status: 'sending' } : prev));
        await pollUntilComplete(id);
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (!campaign) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={campaign.subject}
        breadcrumbs={[{ label: 'Campaigns', href: '/backoffice/campaigns' }]}
        help={
          <p>
            Status: {campaign.status} · Sent {campaign.sentCount}/{campaign.recipientsCount}
          </p>
        }
        actions={
          <>
            {(campaign.status === 'draft' || campaign.status === 'failed') && (
              <Button onClick={handleSend} disabled={sending}>
                {sending ? 'Sending...' : campaign.status === 'failed' ? 'Retry send' : 'Send campaign'}
              </Button>
            )}
            {campaign.status === 'sending' && (
              <span className="text-sm text-muted-foreground">Sending in progress…</span>
            )}
          </>
        }
      />

      <Card>
        <CardHeader>
          <h2 className="font-semibold">Audience</h2>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm">
            Users: {campaign.audienceConfig.includeUsers ? 'Yes' : 'No'} · Prospects:{' '}
            {campaign.audienceConfig.includeProspects ? 'Yes' : 'No'}
          </p>
          <Button variant="outline" size="sm" onClick={handlePreviewRecipients}>
            Preview recipient count
          </Button>
          {recipientCount != null && (
            <p className="text-sm text-muted-foreground">{recipientCount} recipients</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="font-semibold">Email preview</h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field>
            <FieldLabel>Preview recipient email</FieldLabel>
            <div className="flex gap-2">
              <Input value={previewEmail} onChange={(e) => setPreviewEmail(e.target.value)} />
              <Button variant="outline" onClick={handlePreviewEmail}>
                Preview
              </Button>
            </div>
          </Field>
          {previewHtml && (
            <iframe
              title="Email preview"
              srcDoc={previewHtml}
              className="w-full h-96 border rounded-md"
            />
          )}
        </CardContent>
      </Card>

      {recipients.length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold">Recipients ({recipients.length})</h2>
          </CardHeader>
          <CardContent>
            <ul className="text-sm divide-y max-h-64 overflow-auto">
              {recipients.map((r) => (
                <li key={r.id} className="py-1 flex justify-between">
                  <span>{r.email}</span>
                  <span className="text-muted-foreground">{r.status}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
